/**
 * A calendar date — typed, or picked. The field shows the date as the reader
 * writes dates (their locale's numeric order: 31/12/2026, 12/31/2026), takes it
 * typed in that order, and a button beside it opens the calendar in the
 * reader's language with a month and year dropdown (an issue date is often
 * years back); "Clear" empties it.
 *
 * The value is an ISO calendar day, `''` when empty — or, while the person has
 * left text that is not a day the field accepts (not a date, or outside
 * `min`…`max`), that text as typed: the form's schema refuses it and says so,
 * and nothing typed is silently thrown away.
 *
 * Empty, the calendar opens where the answer most likely is: `openOn` when the
 * question knows it (an expiry: the title's usual validity), else the first
 * day that can be picked (a return: the departure, `calendarMin`), else this
 * month.
 *
 * Empty, it shows the format to type in the reader's order and letters
 * ("dd/mm/aaaa", "mm/dd/yyyy") — never a date, which reads as one already
 * filled in (owner, 19 Oct 2026). Those letters are its only words of its
 * own; every other string is the caller's, translated.
 */
import { CalendarDays, X } from 'lucide-react';
import { useTranslation } from '@tonnie/locale';
import { useEffect, useMemo, useState } from 'react';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { Calendar } from '../primitives/calendar';
import { Input } from '../primitives/input';
import { Popover, PopoverContent, PopoverTrigger } from '../primitives/popover';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` of a calendar day — no time zone moves it. */
function isoOf(day: Date): string {
  const m = String(day.getMonth() + 1).padStart(2, '0');
  const d = String(day.getDate()).padStart(2, '0');
  return `${day.getFullYear()}-${m}-${d}`;
}

/** The calendar day of an ISO date, at local midnight (what the calendar compares). */
function dayOf(iso: string): Date | undefined {
  if (!ISO_DAY.test(iso)) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  const day = new Date(y, m - 1, d);
  return isoOf(day) === iso ? day : undefined;
}

type Part = 'day' | 'month' | 'year';

/** How the reader writes a date in numbers: the order of its parts, and a day so written. */
function numericDates(intlLocale: string) {
  const format = new Intl.DateTimeFormat(intlLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const order = format
    .formatToParts(new Date(2000, 0, 2))
    .map((part) => part.type)
    .filter((type): type is Part => type === 'day' || type === 'month' || type === 'year');
  return {
    order,
    /** The pattern the reader types, in their order and separators: "dd/mm/aaaa", "mm/dd/yyyy". */
    pattern: (letters: Record<Part, string>) =>
      format
        .formatToParts(new Date(2000, 0, 2))
        .map((part) =>
          part.type === 'day' || part.type === 'month' || part.type === 'year'
            ? letters[part.type]
            : part.value
        )
        .join(''),
    write: (iso: string) => {
      const day = dayOf(iso);
      return day ? format.format(day) : iso;
    },
    /** The day typed in the reader's order — four-digit year — or null. */
    read: (text: string): string | null => {
      const digits = text.trim();
      const groups = /^\d{8}$/.test(digits)
        ? order.map((part, i) => {
            const at = order.slice(0, i).reduce((n, p) => n + (p === 'year' ? 4 : 2), 0);
            return digits.slice(at, at + (part === 'year' ? 4 : 2));
          })
        : digits.split(/[^\d]+/).filter(Boolean);
      if (groups.length !== 3) return null;
      const by = Object.fromEntries(order.map((part, i) => [part, groups[i]])) as Record<
        Part,
        string
      >;
      if (by.year.length !== 4 || by.month.length > 2 || by.day.length > 2) return null;
      const iso = `${by.year}-${by.month.padStart(2, '0')}-${by.day.padStart(2, '0')}`;
      return dayOf(iso) ? iso : null;
    },
  };
}

export interface DateFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** The APP locale (BCP-47, e.g. "pt-PT") — `useLocale().intlLocale`. */
  intlLocale: string;
  /** The calendar button's name ("Pick the issue date"). */
  calendarLabel: string;
  /** The clear button's name. */
  clearLabel: string;
  /** Days before this one are not accepted — typed or picked. */
  min?: string;
  /**
   * Days before this one cannot be picked in the calendar, but typed they reach
   * the form — an order between two fields (a return after its departure),
   * which the form's schema refuses in its own words.
   */
  calendarMin?: string;
  /** Days after this one are not accepted. */
  max?: string;
  /** The month the calendar opens on while empty. */
  openOn?: string;
  invalid?: boolean;
  describedBy?: string;
  /** The years the dropdown offers. */
  fromYear: number;
  toYear: number;
  className?: string;
}

export function DateField({
  id,
  value,
  onChange,
  intlLocale,
  calendarLabel,
  clearLabel,
  min,
  calendarMin,
  max,
  openOn,
  invalid = false,
  describedBy,
  fromYear,
  toYear,
  className,
}: DateFieldProps) {
  const dates = useMemo(() => numericDates(intlLocale), [intlLocale]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => dates.write(value));
  // The value moves from outside too (a reset, the calendar): the field says it.
  useEffect(() => setText(dates.write(value)), [value, dates]);

  const selected = dayOf(value);
  const firstPick = [min, calendarMin]
    .filter((d): d is string => !!d)
    .sort()
    .pop();
  const before = firstPick ? dayOf(firstPick) : undefined;
  const after = max ? dayOf(max) : undefined;
  const { t } = useTranslation();
  // The format to type, never a date that reads as one already filled in (owner, 19 Oct 2026).
  const pattern = useMemo(
    () =>
      dates.pattern({
        day: t('common.dateFormat.day'),
        month: t('common.dateFormat.month'),
        year: t('common.dateFormat.year'),
      }),
    [dates, t]
  );

  const commit = () => {
    const typed = text.trim();
    if (typed === '') {
      if (value !== '') onChange('');
      return;
    }
    const iso = dates.read(typed);
    const inRange = iso !== null && (!min || iso >= min) && (!max || iso <= max);
    const next = iso !== null && inRange ? iso : typed;
    if (next !== value) onChange(next);
    else setText(dates.write(next));
  };

  return (
    <div className={cn('relative', className)} data-slot="date-field">
      <Input
        id={id}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        inputMode="numeric"
        autoComplete="off"
        placeholder={pattern}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className="h-11 pr-20 tabular-nums"
      />
      <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
        {text !== '' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={clearLabel}
            onClick={() => {
              setText('');
              onChange('');
            }}
            data-testid={`${id}-clear`}
          >
            <X aria-hidden="true" />
          </Button>
        ) : null}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={`${id}-calendar`}
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={calendarLabel}
              aria-controls={id}
            >
              <CalendarDays aria-hidden="true" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              intlLocale={intlLocale}
              captionLayout="dropdown"
              startMonth={new Date(fromYear, 0)}
              endMonth={new Date(toYear, 11)}
              selected={selected}
              defaultMonth={
                selected ?? (openOn ? dayOf(openOn) : undefined) ?? before ?? new Date()
              }
              disabled={[...(before ? [{ before }] : []), ...(after ? [{ after }] : [])]}
              onSelect={(day) => {
                if (!day) return;
                onChange(isoOf(day));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
